import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError } from '../../api';
import { TRANSFER_NACK_REASONS, type TransferNackReason } from '../../api/areas/core';
import { Callout, Field, Section } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, Loading } from '../../components/States';
import { transferReasonWith } from '../../lib/eppResult';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { ActorName, CustomerRef, domainHref, DomainRef, EppResult, Money, RegistryRef, TxRef, useVocab, WorkflowRef } from '../domains/refs';
import { COMMAND_STATE, LIFECYCLE, StatusBadge, TRANSFER } from '../domains/status';
import { EvidenceView, RelatedEvents } from './shared';

/** Cancels our own pending inbound transfer at the registry (with a reason, kept in the audit log). */
function CancelTransfer({ id, onDone }: { id: string; onDone: () => void }) {
  const { t } = useTranslation('domains');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.cancelTransfer(id, b, opts));
  const close = () => {
    setOpen(false);
    if (w.state.phase === 'done') onDone();
  };
  return (
    <>
      <Button type="button" size="sm" variant="destructive" onClick={() => setOpen(true)}>
        {t('transfers.cancel')}
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('transfers.cancelTitle')}</DialogTitle>
            <DialogDescription>{t('transfers.cancelDescription')}</DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void w.run({ reason });
            }}
          >
            <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
            </Field>
            {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
            {w.state.phase === 'done' ? (
              <Callout tone="ok">{w.state.result.status === 'APPLIED' ? t('transfers.cancelled') : t('transfers.cancelPending')}</Callout>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {t('actions.close')}
              </Button>
              <Button type="submit" variant="destructive" disabled={w.busy || w.state.phase === 'done' || !reason.trim()}>
                {t('transfers.cancelSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Answers a pending outbound transfer at the registry: let the domain go, or refuse it on one of
 * the grounds the transfer policy allows. The staff reason is kept in the audit log.
 */
function RespondTransfer({ id, approve, onDone }: { id: string; approve: boolean; onDone: () => void }) {
  const { t } = useTranslation('domains');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [ground, setGround] = useState<TransferNackReason | ''>('');
  const w = useWrite((b: { approve: boolean; nack_reason?: TransferNackReason; reason: string }, opts) => api.respondTransfer(id, b, opts));
  const close = () => {
    setOpen(false);
    if (w.state.phase === 'done') onDone();
  };
  const ready = reason.trim() !== '' && (approve || ground !== '');
  const done = w.state.phase === 'done' ? w.state.result.status : null;
  return (
    <>
      <Button type="button" size="sm" variant={approve ? 'default' : 'destructive'} onClick={() => setOpen(true)}>
        {approve ? t('transfers.approveOut') : t('transfers.rejectOut')}
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{approve ? t('transfers.approveOutTitle') : t('transfers.rejectOutTitle')}</DialogTitle>
            <DialogDescription>{approve ? t('transfers.approveOutDescription') : t('transfers.rejectOutDescription')}</DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void w.run(approve ? { approve, reason } : { approve, nack_reason: ground || undefined, reason });
            }}
          >
            {approve ? null : (
              <Field label={t('transfers.ground')}>
                <Select value={ground} onValueChange={(v) => setGround(v as TransferNackReason)}>
                  <SelectTrigger aria-label={t('transfers.ground')}>
                    <SelectValue placeholder={t('transfers.groundPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSFER_NACK_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t(`transfers.grounds.${r}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
            </Field>
            {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
            {done ? (
              <Callout tone="ok">
                {done === 'APPLIED'
                  ? approve
                    ? t('transfers.approvedOut')
                    : t('transfers.rejectedOut')
                  : done === 'AWAITING_CONFIRMATION'
                    ? t('transfers.respondAwaiting')
                    : t('transfers.respondPending')}
              </Callout>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {t('actions.close')}
              </Button>
              <Button type="submit" variant={approve ? 'default' : 'destructive'} disabled={w.busy || done !== null || !ready}>
                {approve ? t('transfers.approveOutSubmit') : t('transfers.rejectOutSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Why a transfer stopped: the registry's answer as its result (its text folded away), a code the platform records by name, a person's text as typed. */
function RejectReason({ reason }: { reason: string }) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const r = transferReasonWith(reason, (key) => (i18n.exists(key) ? tr(key) : undefined));
  if (!r) return <>{reason}</>;
  return r.kind === 'registry' ? <EppResult code={r.code} message={r.message} /> : <span title={reason}>{r.text}</span>;
}

/** `/transfers/:id`: one registrar transfer with its authorisation, workflow, registry command and history. */
export function TransferPage() {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const L = useLabels();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getTransfer(id, { signal }), [id]);
  const title = result.status === 'success' ? t('transfers.detailTitle', { direction: vocab('direction', result.data.direction), domain: result.data.unicode_name }) : t('transfers.title');
  useDocumentTitle(title);
  const crumbs = [{ label: t('transfers.crumb') }, { label: t('transfers.title'), to: '/transfers' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('transfers.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('transfers.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('transfers.notFound') : t('transfers.loadFailed')} />
      </>
    );
  }
  const x = result.data;
  const pending = x.status === 'REGISTRY_PENDING' || x.status === 'REQUESTED' || x.status === 'AUTHORIZED';

  return (
    <>
      <PageHeader
        title={title}
        crumbs={[...crumbs, { label: x.unicode_name }]}
        subtitle={t('transfers.detailSubtitle', { domain: x.unicode_name })}
        actions={
          <span className="flex items-center gap-2">
            <StatusBadge map={TRANSFER} value={x.status} />
            {x.direction === 'IN' && x.status === 'REGISTRY_PENDING' ? <CancelTransfer id={x.id} onDone={result.reload} /> : null}
            {x.direction === 'OUT' && x.status === 'REGISTRY_PENDING' ? (
              <>
                <RespondTransfer id={x.id} approve={false} onDone={result.reload} />
                <RespondTransfer id={x.id} approve onDone={result.reload} />
              </>
            ) : null}
          </span>
        }
      />
      {pending && x.auto_approve_at ? (
        <Callout tone="warn" className="mb-6">
          {t('transfers.autoApproveAt')}: {formatDateTime(x.auto_approve_at)} ({formatRelative(x.auto_approve_at)})
        </Callout>
      ) : null}
      {x.reject_reason ? (
        <Callout tone="crit" title={t('transfers.rejectReason')} className="mb-6">
          <RejectReason reason={x.reject_reason} />
        </Callout>
      ) : null}
      <div className="grid grid-cols-1 gap-6">
        <Section title={t('transfers.overview')}>
          <KeyValue
            columns={3}
            rows={[
              [t('transfers.domain'), <DomainRef key="d" ascii={x.ascii_name} unicode={x.unicode_name} id={x.domain_id} tab="transfers" />],
              [t('transfers.direction'), vocab('direction', x.direction)],
              [t('transfers.status'), <StatusBadge key="s" map={TRANSFER} value={x.status} />],
              [t('transfers.registryStatus'), x.registry_status ? vocab('trStatus', x.registry_status) : '—'],
              [t('transfers.other'), x.other_registrar ?? '—'],
              [t('transfers.customer'), <CustomerRef key="c" id={x.customer_id} name={x.customer_name} />],
              [t('transfers.registry'), <RegistryRef key="r" code={x.registry_code} name={x.registry_name} />],
              [t('transfers.lifecycle'), <StatusBadge key="l" map={LIFECYCLE} value={x.lifecycle_state} />],
              [t('transfers.authInfo'), x.has_auth_info ? t('transfers.authInfoYes') : t('transfers.authInfoNo')],
              [t('transfers.requested'), formatDateTime(x.requested_at)],
              [t('transfers.requestedBy'), <ActorName key="rb" type={x.requested_by_type} name={x.requested_by_name} />],
              x.registry_pending_at ? [t('transfers.registryPending'), formatDateTime(x.registry_pending_at)] : null,
              x.auto_approve_at ? [t('transfers.autoApproveAt'), formatDateTime(x.auto_approve_at)] : null,
              x.decided_at ? [t('transfers.decidedAt'), formatDateTime(x.decided_at)] : null,
              x.decided_by_type ? [t('transfers.decidedBy'), <ActorName key="db" type={x.decided_by_type} name={x.decided_by_name} />] : null,
              x.completed_at ? [t('transfers.completedAt'), formatDateTime(x.completed_at)] : null,
              [t('common.id'), <CopyText key="id" value={x.id} />],
            ]}
          />
        </Section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section title={t('transfers.follow')}>
            <KeyValue
              rows={[
                [
                  t('transfers.workflow'),
                  <WorkflowRef
                    key="w"
                    id={x.workflow_id}
                    type={x.workflow_type}
                    state={x.workflow_state}
                    sub={x.workflow_step ? t('transfers.step', { step: L.workflowStep(x.workflow_step) }) : undefined}
                  />,
                ],
                [
                  t('transfers.command'),
                  x.registry_transaction_id ? (
                    <span key="tx" className="flex flex-wrap items-center gap-2">
                      <TxRef id={x.registry_transaction_id} operation={x.registry_operation} sub={x.registry_cltrid} />
                      {x.registry_transaction_state ? <StatusBadge map={COMMAND_STATE} value={x.registry_transaction_state} /> : null}
                    </span>
                  ) : (
                    '—'
                  ),
                ],
                [
                  t('transfers.order'),
                  x.order_id ? (
                    <EntityRef
                      key="o"
                      label={L.order({ first_domain: x.order_first_domain, item_count: x.order_item_count, created_at: x.order_created_at })}
                      to={`/orders/${encodeURIComponent(x.order_id)}`}
                      id={x.order_id}
                      sub={
                        <>
                          {x.order_item_status ? `${vocab('orderStatus', x.order_item_status)} · ` : null}
                          <Money amount={x.order_item_amount} currency={x.order_item_currency} />
                        </>
                      }
                    />
                  ) : (
                    <span key="o" className="text-sm text-muted-foreground">
                      {t('transfers.noOrder')}
                    </span>
                  ),
                ],
              ]}
            />
          </Section>
          <Section title={t('transfers.evidence')} description={t('transfers.evidenceDescription')}>
            <EvidenceView evidence={x.authorization_evidence} empty={t('transfers.noEvidence')} />
          </Section>
        </div>

        <Section flush title={t('transfers.events')}>
          <RelatedEvents events={x.events} empty={t('transfers.noEvents')} historyHref={domainHref(x.ascii_name, 'history')} />
        </Section>
      </div>
    </>
  );
}
